import type {
  SipnavApiMapAvailability,
  SipnavApiMapCapability,
  SipnavApiMapOperation,
} from '@/services/api';

export type SipnavApiMapAvailabilityFilter = 'all' | SipnavApiMapAvailability;

function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function isSubsequence(query: string, candidate: string): boolean {
  if (query.length < 4 || candidate.length - query.length > 3) return false;
  let queryIndex = 0;
  for (const character of candidate) {
    if (character === query[queryIndex]) queryIndex += 1;
    if (queryIndex === query.length) return true;
  }
  return false;
}

function fuzzyTokenMatches(queryToken: string, candidateWords: string[]): boolean {
  return candidateWords.some((word) => (
    word.includes(queryToken)
    || isSubsequence(queryToken, word)
  ));
}

function operationSearchText(
  operation: SipnavApiMapOperation,
  capabilitiesById: ReadonlyMap<string, SipnavApiMapCapability>
): string {
  const capabilities = operation.capabilityIds
    .map((id) => capabilitiesById.get(id))
    .filter((capability): capability is SipnavApiMapCapability => Boolean(capability));
  return normalize([
    operation.operationKey,
    operation.operationId,
    operation.method,
    operation.path,
    operation.summary ?? '',
    operation.description ?? '',
    operation.apiFamilies.join(' '),
    operation.documentedEffect,
    JSON.stringify(operation.parameters),
    JSON.stringify(operation.requestSchemas),
    JSON.stringify(operation.responseSchemas),
    JSON.stringify(operation.observedResponseStructure),
    capabilities.map((capability) => (
      `${capability.id} ${capability.title} ${capability.description}`
    )).join(' '),
  ].join(' '));
}

function matchScore(searchText: string, normalizedQuery: string): number | null {
  if (!normalizedQuery) return 0;
  const directIndex = searchText.indexOf(normalizedQuery);
  if (directIndex >= 0) return directIndex;
  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  const candidateWords = searchText.split(' ').filter(Boolean);
  if (!queryTokens.every((token) => fuzzyTokenMatches(token, candidateWords))) return null;
  return 10_000 + queryTokens.length;
}

export function filterSipnavApiOperations(input: {
  operations: SipnavApiMapOperation[];
  capabilities: SipnavApiMapCapability[];
  query: string;
  availability: SipnavApiMapAvailabilityFilter;
  family: string | null;
}): SipnavApiMapOperation[] {
  const normalizedQuery = normalize(input.query);
  const capabilitiesById = new Map(
    input.capabilities.map((capability) => [capability.id, capability])
  );
  return input.operations
    .filter((operation) => (
      input.availability === 'all' || operation.availability === input.availability
    ))
    .filter((operation) => (
      !input.family || operation.apiFamilies.includes(input.family)
    ))
    .map((operation) => ({
      operation,
      score: matchScore(operationSearchText(operation, capabilitiesById), normalizedQuery),
    }))
    .filter((candidate): candidate is { operation: SipnavApiMapOperation; score: number } => (
      candidate.score !== null
    ))
    .sort((left, right) => (
      left.score - right.score
      || left.operation.path.localeCompare(right.operation.path)
      || left.operation.method.localeCompare(right.operation.method)
    ))
    .map(({ operation }) => operation);
}

export function listSipnavApiFamilies(operations: SipnavApiMapOperation[]): Array<{
  name: string;
  count: number;
}> {
  const counts = new Map<string, number>();
  for (const operation of operations) {
    for (const family of operation.apiFamilies) {
      counts.set(family, (counts.get(family) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((left, right) => left.name.localeCompare(right.name));
}
